"use client";

import { useState, useEffect, useCallback } from "react";
import { deleteInfo, getPublicInfo, postInfo, putInfo } from "utils/api";
import { extractErrorMessage, PaginatedApiResponse, Severity } from "utils/helper/ApiResponses";
import { Step } from "utils/types/Info.types";


interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

export function useStepManager() {
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // --- Snackbar Handlers ---
  const handleSnackbarOpen = useCallback((message: string, severity: Severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Data Fetching ---
  const fetchSteps = useCallback(async () => {
    setLoading(true);
    try {
      // Use the new getPublicInfo service
      const result = await getPublicInfo<Step>(['steps'], {
        page: 1,
        limit: 100, // Fetch all for now
        sortBy: 'step', // You can make this dynamic if needed
        order: 'asc',
      });
      
      const stepsList = (result.data as PaginatedApiResponse<Step>).data;
      setSteps(stepsList || []);
      
    } catch (err: any) {
      console.error("Error fetching steps:", err);
      handleSnackbarOpen(extractErrorMessage(err).message, "error");
    } finally {
      setLoading(false);
    }
  }, [handleSnackbarOpen]);

  // Fetch steps when the component mounts
  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  // --- CRUD Operations ---
  const handleSaveStep = async (step: Omit<Step, 'id'> & { id?: string }) => {
    setIsSaving(true);
    try {
      if (step.id) {
        // Update existing step
        await putInfo(['steps'], step);
        handleSnackbarOpen("Étape mise à jour avec succès!", "success");
      } else {
        // Create new step
        await postInfo(['steps'], step);
        handleSnackbarOpen("Étape créée avec succès!", "success");
      }
      fetchSteps(); // Refresh the list
      handleCloseDialog();
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStep = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette étape ?")) return;

    setIsSaving(true);
    try {
      await deleteInfo(['steps'], id);
      handleSnackbarOpen("Étape supprimée avec succès!", "success");
      fetchSteps(); // Refresh the list
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // --- Dialog and UI Handlers ---
  const handleExpandStep = (stepId: string) => {
    setExpandedStepId(expandedStepId === stepId ? null : stepId);
  };

  const handleOpenAddDialog = () => {
    setCurrentStep(null);
    setOpenDialog(true);
  };
  
  const handleOpenEditDialog = (step: Step) => {
    setCurrentStep(step);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentStep(null);
  };

  return {
    steps,
    loading,
    isSaving,
    openDialog,
    currentStep,
    expandedStepId,
    snackbar,
    handleSaveStep,
    handleDeleteStep,
    handleExpandStep,
    handleSnackbarClose,
    handleOpenAddDialog,
    handleOpenEditDialog,
    handleCloseDialog,
  };
}